import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { DetailedScorecard } from "../../components/leaderboard/DetailedScorecard";
import { CourseConfig } from "../../models/teeBox";
import { useLeaderboardStore } from "../../store/leaderboardStore";
import { usePublicStore } from "../../store/publicStore";
import { MatchPlayDetailedScorecard } from "../../components/leaderboard/MatchPlayDetailedScorecard";

export function PlayerDetails() {
  const { playerId } = useParams<{ playerId: string }>();
  const { playerLeaderboard, leaderboardMatchId } = useLeaderboardStore();
  const { currentSeason, matchSetups } = usePublicStore();

  const [player, setPlayer] = useState<any | null>(null);
  const [courseConfig, setCourseConfig] = useState<CourseConfig | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMatchPlay, setIsMatchPlay] = useState(false);

  useEffect(() => {
    if (playerLeaderboard && currentSeason && matchSetups) {
      const pl = playerLeaderboard.find((p) => p.id === playerId);
      if (pl) {
        setPlayer(pl);
      } else {
        setError("Player not found");
      }
      const matchConfig = matchSetups.find(
        (m) => m.matchId === leaderboardMatchId
      );
      if (matchConfig) {
        setIsMatchPlay(matchConfig.gameFormat === "matchplay");
        const courseConfig = currentSeason.courseConfigs?.find(
          (c) => c.courseName === matchConfig.courseName
        );
        if (courseConfig) {
          setCourseConfig(courseConfig);
          setLoading(false);
        } else {
          setError("Course configuration not available");
          setLoading(false);
        }
      } else {
        setError("Course configuration not available");
        setLoading(false);
      }
    }
  }, [currentSeason, matchSetups, leaderboardMatchId, playerLeaderboard]);

  if (!player) {
    return (
      <div className="text-center py-12 pt-2">
        <h2 className="text-2xl font-bold text-gray-900">Player not found</h2>
        <Link
          to={
            window.location.pathname.startsWith("/currentSeason")
              ? "/currentSeason/leaderboard"
              : "/leaderboard"
          }
          className="mt-4 inline-flex items-center text-gray-700 dark:text-gray-300 cursor-pointer hover:text-gray-900 dark:hover:text-white"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Leaderboard
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12 pt-2">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error || !courseConfig) {
    return (
      <div className="text-center py-12 pt-2">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          {error || "Course configuration not available"}
        </h2>
        <Link
          to={
            window.location.pathname.startsWith("/currentSeason")
              ? "/currentSeason/leaderboard"
              : "/leaderboard"
          }
          className="inline-flex items-center text-gray-700 dark:text-gray-300 cursor-pointer hover:text-gray-900 dark:hover:text-white"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Leaderboard
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-2 mt-2">
      <div className="flex items-center justify-between mb-2">
        <Link
          to={
            window.location.pathname.startsWith("/currentSeason")
              ? "/currentSeason/leaderboard"
              : "/leaderboard"
          }
          className="inline-flex items-center text-gray-700 dark:text-gray-300 cursor-pointer hover:text-gray-900 dark:hover:text-white"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Leaderboard
        </Link>
      </div>

      {isMatchPlay && (
        <MatchPlayDetailedScorecard
          player={player}
          courseConfig={courseConfig}
        />
      )}
      {!isMatchPlay && (
        <DetailedScorecard player={player} courseConfig={courseConfig} />
      )}
    </div>
  );
}
